import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Building2,
  Palette,
  Package,
  FileText,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Lock,
  FileEdit,
} from 'lucide-react';
import {
  adminApi,
  getAdminApiKey,
  setAdminApiKey,
  clearAdminApiKey,
  getSession,
  logoutSession,
  loginTenantAdminSession,
} from '../../lib/api.js';
import { useTenant } from '../../context/TenantContext.jsx';
import { useEditMode } from '../../context/EditModeContext.jsx';
import { AdminAuthGuard } from './AdminAuthGuard.jsx';
import { AdminHeader } from './AdminHeader.jsx';
import { TenantsTab } from './TenantsTab.jsx';
import { ThemeCustomizerTab } from './ThemeCustomizerTab.jsx';
import { CatalogAssortmentTab } from './CatalogAssortmentTab.jsx';
import { InquiriesQuotesTab } from './InquiriesQuotesTab.jsx';
import { CatalogSyncTab } from './CatalogSyncTab.jsx';
import { ContentEditorTab } from './ContentEditorTab.jsx';

export function AdminDashboardView({
  section = 'tenants',
  onNavigate,
}) {
  const { tenant: contextTenant } = useTenant();
  const { enterEditMode, recheckSession } = useEditMode();

  // Active Tab: normalize section to valid tab keys
  const validTabs = ['tenants', 'theme', 'assortment', 'inquiries', 'sync', 'content'];
  const initialTab = validTabs.includes(section) ? section : 'tenants';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Sync tab with external route change
  useEffect(() => {
    if (section && validTabs.includes(section) && section !== activeTab) {
      setActiveTab(section);
    }
  }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auth State
  const [apiKey, setApiKey] = useState(() => getAdminApiKey());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [validatingAuth, setValidatingAuth] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('validating'); // 'connected' | 'offline' | 'validating'

  // Role State (Issue #1): 'admin' (API-Key/Bearer) | 'tenant_admin' (Demo-Session-Cookie)
  const [role, setRole] = useState(null);
  const [sessionTenantId, setSessionTenantId] = useState(null);

  // Data State
  const [tenants, setTenants] = useState([]);
  const [activeTenant, setActiveTenant] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [savingData, setSavingData] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);

  // Authenticate & Load Initial Data
  const authenticateAndLoad = useCallback(async (keyToValidate) => {
    const key = keyToValidate || getAdminApiKey();
    if (!key) {
      setValidatingAuth(false);
      setAuthError(null);
      setIsAuthenticated(false);
      return;
    }

    setValidatingAuth(true);
    setAuthError(null);
    setConnectionStatus('validating');

    try {
      // Validate by loading tenants
      const tenantsRes = await adminApi.getTenants(key);
      let loadedInquiries = [];
      let inquiriesFailed = false;
      try {
        const inquiriesRes = await adminApi.getInquiries({}, key);
        loadedInquiries = inquiriesRes?.inquiries || [];
      } catch (inqErr) {
        console.error('Failed to load inquiries:', inqErr);
        inquiriesFailed = true;
        setDashboardError('Anfragen konnten nicht geladen werden (' + (inqErr.message || 'Serverfehler') + ').');
      }

      setAdminApiKey(key);
      setApiKey(key);
      setIsAuthenticated(true);
      if (inquiriesFailed) {
        setConnectionStatus('offline');
      } else {
        setConnectionStatus('connected');
      }

      const loadedTenants = tenantsRes?.tenants || [];
      setTenants(loadedTenants);

      // Set active tenant (prefer matching contextTenant, or first tenant)
      const matched = loadedTenants.find((t) => t.id === contextTenant?.id || t.slug === contextTenant?.slug);
      setActiveTenant(matched || loadedTenants[0] || null);

      setInquiries(loadedInquiries);
    } catch (err) {
      console.error('Admin Auth Failure:', err);
      setIsAuthenticated(false);
      setConnectionStatus('offline');
      if (err.status === 401 || err.status === 403) {
        setAuthError('Ungültiger API-Schlüssel. Zugriff verweigert (HTTP ' + err.status + ').');
        clearAdminApiKey();
      } else {
        setAuthError(err.message || 'Authentifizierungsfehler');
      }
    } finally {
      setValidatingAuth(false);
    }
  }, [contextTenant]);

  // Demo-/Interessenten-Session erkennen (Cookie-basiert, Rolle tenant_admin):
  // lädt NUR den eigenen Mandanten; alle systemweiten Admin-Routen bleiben gesperrt.
  const loadTenantAdminSession = useCallback(async () => {
    setValidatingAuth(true);
    try {
      const session = await getSession();
      if (!session?.authenticated || session?.role !== 'tenant_admin' || !session?.tenantId) {
        return false;
      }
      const res = await adminApi.getTenant(session.tenantId, null);
      const ownTenant = res?.tenant || null;
      if (!ownTenant) {
        throw new Error('Demo-Mandant konnte nicht geladen werden.');
      }
      setRole('tenant_admin');
      setSessionTenantId(session.tenantId);
      setTenants([ownTenant]);
      setActiveTenant(ownTenant);
      setIsAuthenticated(true);
      setConnectionStatus('connected');
      return true;
    } catch (err) {
      console.error('Tenant-Admin-Session-Fehler:', err);
      return false;
    } finally {
      setValidatingAuth(false);
    }
  }, []);

  // Initial Auth Check on mount: session role first (tenant_admin), then API key
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sessionOk = await loadTenantAdminSession();
      if (cancelled || sessionOk) return;
      const storedKey = getAdminApiKey();
      if (storedKey) {
        authenticateAndLoad(storedKey);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadTenantAdminSession, authenticateAndLoad]);

  // Tenant-Admins landen immer im Inhalte-Tab, wenn der geroutete Tab nicht erlaubt ist
  useEffect(() => {
    if (role === 'tenant_admin' && !['content', 'theme', 'assortment'].includes(activeTab)) {
      setActiveTab('content');
    }
  }, [role, activeTab]);

  // Handle Tab Switch
  const handleTabChange = (newTab) => {
    const allowedTabs = role === 'tenant_admin' ? ['content', 'theme', 'assortment'] : validTabs;
    if (!allowedTabs.includes(newTab)) newTab = role === 'tenant_admin' ? 'content' : 'tenants';
    setActiveTab(newTab);
    if (onNavigate) {
      onNavigate(`/admin/${newTab}`);
    }
  };

  // Handle Logout
  const handleLogout = () => {
    if (role === 'tenant_admin') {
      // Session-Cookie serverseitig löschen (best effort)
      logoutSession().catch(() => {});
    }
    clearAdminApiKey();
    setApiKey('');
    setRole(null);
    setSessionTenantId(null);
    setIsAuthenticated(false);
    setTenants([]);
    setInquiries([]);
    setConnectionStatus('offline');
  };

  // Demo-/Interessenten-Login (Rolle tenant_admin, Cookie-Session)
  const handleTenantAdminLogin = useCallback(async (login, password) => {
    setValidatingAuth(true);
    setAuthError(null);
    try {
      await loginTenantAdminSession(login, password);
      const ok = await loadTenantAdminSession();
      if (!ok) {
        setAuthError('Demo-Anmeldung fehlgeschlagen. Bitte Zugangsdaten prüfen.');
        return;
      }
      // Storefront-First (Issue #3): Der Kunde landet nach dem Login direkt in
      // seinem Shop — im Bearbeiten-Modus, nicht im Admin-Dashboard.
      enterEditMode();
      recheckSession();
      onNavigate('/');
    } catch (err) {
      if (err.status === 401 || err.message === 'invalid_credentials') {
        setAuthError('Ungültige Anmeldedaten. Bitte überprüfen Sie Benutzername und Passwort.');
      } else if (err.status === 503 || err.message === 'auth_not_configured') {
        setAuthError('Der Authentifizierungsdienst ist auf dem Server nicht konfiguriert.');
      } else {
        setAuthError(err.message || 'Demo-Anmeldung fehlgeschlagen.');
      }
    } finally {
      setValidatingAuth(false);
    }
  }, [loadTenantAdminSession, enterEditMode, recheckSession, onNavigate]);

  // ---------------------------------------------------------------------------
  // Data Mutation Handlers
  // ---------------------------------------------------------------------------

  // Create Tenant
  const handleCreateTenant = async (payload) => {
    setSavingData(true);
    setDashboardError(null);
    try {
      const res = await adminApi.createTenant(payload, apiKey);
      if (res?.tenant) {
        setTenants((prev) => [res.tenant, ...prev]);
        setActiveTenant(res.tenant);
      }
    } catch (err) {
      console.error('Failed to create tenant:', err);
      setDashboardError('Mandant konnte nicht angelegt werden (' + (err.message || 'Serverfehler') + ').');
      throw err;
    } finally {
      setSavingData(false);
    }
  };

  // Update Tenant
  const handleUpdateTenant = async (tenantId, payload) => {
    setSavingData(true);
    setDashboardError(null);
    try {
      const res = await adminApi.updateTenant(tenantId, payload, apiKey);
      const updated = res?.tenant;

      setTenants((prev) =>
        prev.map((t) => {
          if (t.id === tenantId || t.slug === tenantId) {
            return {
              ...t,
              ...payload,
              ...(updated || {}),
              settings: {
                ...(t.settings || {}),
                ...(payload.settings || {}),
              },
            };
          }
          return t;
        })
      );

      if (activeTenant && (activeTenant.id === tenantId || activeTenant.slug === tenantId)) {
        setActiveTenant((prev) => ({
          ...prev,
          ...payload,
          ...(updated || {}),
          settings: {
            ...(prev.settings || {}),
            ...(payload.settings || {}),
          },
        }));
      }
    } catch (err) {
      console.error('Failed to update tenant:', err);
      setDashboardError('Mandanten-Einstellungen konnten nicht gespeichert werden (' + (err.message || 'Serverfehler') + ').');
      throw err;
    } finally {
      setSavingData(false);
    }
  };

  // Update Inquiry Status
  const handleUpdateInquiryStatus = async (inquiryId, newStatus, notes) => {
    setSavingData(true);
    setDashboardError(null);
    try {
      await adminApi.updateInquiryStatus(inquiryId, newStatus, notes, apiKey);
      setInquiries((prev) =>
        prev.map((inq) => {
          if (inq.id === inquiryId || inq.inquiryNumber === inquiryId) {
            return {
              ...inq,
              status: newStatus,
              notes: notes !== undefined ? notes : inq.notes,
              updatedAt: new Date().toISOString(),
            };
          }
          return inq;
        })
      );
    } catch (err) {
      console.error('Failed to update inquiry status:', err);
      setDashboardError('Status-Aktualisierung fehlgeschlagen (' + (err.message || 'Serverfehler') + '). Keine Änderungen gespeichert.');
      throw err;
    } finally {
      setSavingData(false);
    }
  };

  // Trigger Catalog Sync
  const handleTriggerSync = async (payload) => {
    return adminApi.triggerCatalogImport(payload, apiKey);
  };

  const adminNavTabs = [
    { key: 'tenants', label: 'Mandanten', icon: Building2, count: tenants.length },
    { key: 'theme', label: 'Theme & Vorschau', icon: Palette },
    { key: 'assortment', label: 'Sortiment & Preise', icon: Package },
    { key: 'inquiries', label: 'Anfragen & Angebote', icon: FileText, count: inquiries.length },
    { key: 'sync', label: 'Katalog-Sync', icon: RefreshCw },
    { key: 'content', label: 'Inhalte & CMS', icon: FileEdit },
  ];
  const tenantAdminNavTabs = [
    { key: 'content', label: 'Inhalte & CMS', icon: FileEdit },
    { key: 'theme', label: 'Theme & Vorschau', icon: Palette },
    { key: 'assortment', label: 'Sortiment & Preise', icon: Package },
  ];
  const navTabs = role === 'tenant_admin' ? tenantAdminNavTabs : adminNavTabs;

  return (
    <AdminAuthGuard
      apiKey={apiKey}
      isAuthenticated={isAuthenticated}
      validating={validatingAuth}
      error={authError}
      onAuthenticate={authenticateAndLoad}
      onTenantAdminLogin={handleTenantAdminLogin}
      role={role}
    >
      <div className="min-h-screen bg-[var(--background)] pb-16">
        {/* Admin Top Header */}
        <AdminHeader
          activeTenant={activeTenant}
          tenants={tenants}
          onSelectTenant={setActiveTenant}
          onNavigate={onNavigate}
          onLogout={handleLogout}
          connectionStatus={connectionStatus}
          role={role}
          onRefresh={() => (role === 'tenant_admin' ? loadTenantAdminSession() : authenticateAndLoad(apiKey))}
          refreshing={validatingAuth}
        />

        <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8 space-y-6">
          {/* Real Error Banner */}
          {dashboardError && (
            <div
              role="alert"
              aria-live="polite"
              className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900 flex items-center justify-between shadow-2xs"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" aria-hidden="true" />
                <span>{dashboardError}</span>
              </div>
              <button
                type="button"
                onClick={() => setDashboardError(null)}
                className="text-xs font-semibold text-red-700 hover:text-red-900 underline ml-4 cursor-pointer"
              >
                Schließen
              </button>
            </div>
          )}
          {/* Main Navigation Tabs — gruppiert: Shop-Werkzeuge (auch für Mandanten) | System (nur ASL) */}
          <div
            role="tablist"
            aria-label="Admin Dashboard Navigation"
            className="border border-[var(--border)] bg-[var(--surface,white)] rounded-xl shadow-2xs p-1.5 flex items-center gap-1.5 overflow-x-auto"
          >
            {(role === 'tenant_admin'
              ? [{ label: null, tabs: tenantAdminNavTabs }]
              : [
                  {
                    label: 'Shop-Werkzeuge',
                    tabs: adminNavTabs.filter((t) => ['content', 'theme', 'assortment'].includes(t.key)),
                  },
                  {
                    label: 'System (nur ASL)',
                    tabs: adminNavTabs.filter((t) => ['tenants', 'inquiries', 'sync'].includes(t.key)),
                  },
                ]
            ).map((group, gi) => (
              <div key={gi} className="flex items-center gap-1.5 shrink-0">
                {group.label && (
                  <span className="px-2.5 text-[10px] font-bold uppercase tracking-wider text-[var(--secondary)]/70 whitespace-nowrap select-none">
                    {group.label}
                  </span>
                )}
                {group.tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => handleTabChange(tab.key)}
                      className={`flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                        isActive
                          ? 'bg-[var(--primary)] text-[var(--on-primary,white)] shadow-xs'
                          : 'text-[var(--secondary)] hover:bg-[var(--surface-container-low,#f1f5f9)] hover:text-[var(--foreground)]'
                      }`}
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                      <span>{tab.label}</span>
                      {tab.count !== undefined && (
                        <span
                          className={`rounded-full px-1.5 py-0.2 text-[10px] ${
                            isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-[var(--secondary)]'
                          }`}
                        >
                          {tab.count}
                        </span>
                      )}
                    </button>
                  );
                })}
                {gi === 0 && role !== 'tenant_admin' && (
                  <div className="mx-1.5 h-6 w-px bg-[var(--border)] shrink-0" aria-hidden="true" />
                )}
              </div>
            ))}
          </div>

          {/* Active Tab View */}
          <div className="transition-all duration-200">
            {activeTab === 'tenants' && (
              <TenantsTab
                tenants={tenants}
                onSelectTenant={setActiveTenant}
                onCreateTenant={handleCreateTenant}
                onUpdateTenant={handleUpdateTenant}
                onSwitchTab={handleTabChange}
                saving={savingData}
              />
            )}

            {activeTab === 'theme' && (
              <ThemeCustomizerTab
                tenants={tenants}
                activeTenant={activeTenant}
                onSelectTenant={setActiveTenant}
                onUpdateTenant={handleUpdateTenant}
                saving={savingData}
              />
            )}

            {activeTab === 'assortment' && (
              <CatalogAssortmentTab
                tenants={tenants}
                activeTenant={activeTenant}
                onSelectTenant={setActiveTenant}
                apiKey={apiKey}
              />
            )}

            {activeTab === 'inquiries' && (
              <InquiriesQuotesTab
                inquiries={inquiries}
                tenants={tenants}
                onUpdateInquiryStatus={handleUpdateInquiryStatus}
                updating={savingData}
              />
            )}

            {activeTab === 'sync' && (
              <CatalogSyncTab
                tenants={tenants}
                onTriggerSync={handleTriggerSync}
                syncing={savingData}
              />
            )}

            {activeTab === 'content' && (
              <ContentEditorTab
                activeTenant={activeTenant}
                apiKey={apiKey}
                saving={savingData}
              />
            )}
          </div>
        </div>
      </div>
    </AdminAuthGuard>
  );
}
